import React, { useState, useEffect } from 'react';

const Calendar = ({onDateSelect, initialDate = null}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startDate, setStartDate] = useState(initialDate ? new Date(initialDate) : null); 
  const [endDate, setEndDate] = useState(null);
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  
  useEffect(() => {
    if (initialDate && !startDate) {
      const date = new Date(initialDate);
      date.setHours(0, 0, 0, 0);
      
      setStartDate(date);
      setIsSelectingRange(!isCurrentDate(date));
      
      if (onDateSelect) {
        onDateSelect({
          startDate: date,
          endDate: isCurrentDate(date) ? null : null,
          isSelectingRange: !isCurrentDate(date)
        });
      }
    }
  }, [initialDate]); 

  const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  const isCurrentDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate.getTime() === today.getTime();
  }
  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate >= today;
  };

  const canGoToPrevMonth = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    
    return currentMonth > today;
  };

  const canGoToNextMonth = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    const maxMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1); 
    
    return nextMonth <= maxMonth;
  };

  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); 
    
    const calendar = [];
    
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = 0; i < startingDay; i++) {
      calendar.push({
        day: prevMonthLastDay - startingDay + i + 1,
        month: 'prev',
        date: new Date(year, month - 1, prevMonthLastDay - startingDay + i + 1),
        disabled: true
      });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      calendar.push({
        day,
        month: 'current',
        date,
        disabled: !isFutureDate(date) 
      });
    }
    
    const totalCells = 42; 
    const remainingCells = totalCells - calendar.length;
    for (let i = 1; i <= remainingCells; i++) {
      calendar.push({
        day: i,
        month: 'next',
        date: new Date(year, month + 1, i),
        disabled: true
      });
    }
    
    return calendar;
  };

  const handleDayClick = (dayInfo) => {
    if (dayInfo.disabled || dayInfo.month !== 'current') return;
    
    const clickedDate = new Date(dayInfo.date);
    clickedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (clickedDate < today) {
      return; 
    }
    
    let newStartDate = startDate;
    let newEndDate = endDate;
    let newIsSelectingRange = isSelectingRange;
    
    if (!isSelectingRange || !startDate) {
      newStartDate = clickedDate;
      newEndDate = null;
      newIsSelectingRange = !isCurrentDate(clickedDate);
    } else {
      if (isCurrentDate(startDate)) {
        newStartDate = clickedDate;
        newEndDate = null;
        newIsSelectingRange = !isCurrentDate(clickedDate);
      } else {
        if (clickedDate < startDate) {
          newStartDate = clickedDate;
          newEndDate = startDate;
        } else {
          newEndDate = clickedDate;
        }
        newIsSelectingRange = false;
      }
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setIsSelectingRange(newIsSelectingRange);

    if (isCurrentDate(newStartDate)) {
      setEndDate(null);
      newEndDate = null;
      setIsSelectingRange(false);
      newIsSelectingRange = false;
    }


    if (onDateSelect) {
      onDateSelect({
        startDate: newStartDate,
        endDate: isCurrentDate(newStartDate) ? null : newEndDate, 
        isSelectingRange: newIsSelectingRange
      });
    }
  };

  const isDateInRange = (date) => {
    if (!startDate) return false;
    
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    if (endDate) {
      return checkDate >= startDate && checkDate <= endDate;
    } else if (isSelectingRange) {
      return checkDate.getTime() === startDate.getTime();
    }
    return false;
  };

  const isStartDate = (date) => {
    if (!startDate) return false;
    return date.getTime() === startDate.getTime();
  };

  const isEndDate = (date) => {
    if (!endDate) return false;
    return date.getTime() === endDate.getTime();
  };

  const goToPrevMonth = () => {
    if (!canGoToPrevMonth()) return;
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    if (!canGoToNextMonth()) return;
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const calendarDays = generateCalendar();

  return (
    <div className='flex items-center justify-center'>
      <div className="w-[450px]">
  
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPrevMonth}
            disabled={!canGoToPrevMonth()}
            className={`p-2 transition-colors rounded-full ${canGoToPrevMonth() ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="#184A7D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <h2 className="text-base tracking-widest text-center md:text-lg font-itcbold text-blue">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          
          <button
            onClick={goToNextMonth}
            disabled={!canGoToNextMonth()}
            className={`p-2 transition-colors rounded-full ${canGoToNextMonth() ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="#184A7D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
      
        <div className="grid grid-cols-7 mt-2 text-center">
          {dayNames.map((day, index) => (
            <div key={index}>
              <div
                  
                  className={`font-itcbold text-base md:text-lg
                      ${index === 0 ? 'text-green' : 'text-blue'} `}>
                  {day}
              </div>
              <div className='w-full bg-blue h-[2px]'></div>
            </div>
          ))}
  
          {calendarDays.map((dayInfo, index) => {
            const isInRange = isDateInRange(dayInfo.date);
            const isStart = isStartDate(dayInfo.date);
            const isEnd = isEndDate(dayInfo.date);
            const isSunday = dayInfo.date.getDay() === 0;
            const isToday = isCurrentDate(dayInfo.date);
            const isPast = !isFutureDate(dayInfo.date);
            
            return (
              <button
                key={index}
                onClick={() => handleDayClick(dayInfo)}
                disabled={dayInfo.disabled}
                className={`
                  p-2 rounded-full text-center transition-all duration-200
                  font-itcbold text-base md:text-lg
                  ${dayInfo.disabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-blue/10'}
                  ${dayInfo.month !== 'current' ? 'text-gray-400' : ''}
                  ${isInRange ? 'bg-blue/20' : ''}
                  ${isStart || isEnd ? 'bg-blue/25 text-white' : ''}
                  ${isSunday && !dayInfo.disabled ? 'text-red-500' : ''}
                  ${index % 7 === 0 && !dayInfo.disabled ? 'text-green' : ''}
                  ${!dayInfo.disabled && dayInfo.month === 'current' && !isSunday ? 'text-black' : ''}
                  ${isToday ? 'ring-2 ring-green' : ''}
                  ${isPast ? 'text-gray-300' : ''}
                `}
              >
                {dayInfo.day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Calendar;